import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import profileDataService from '../../services/profileDataService';
import ProfileTaskGenerationService from '../../services/ProfileTaskGenerationService';
import { supabase } from '../../lib/supabase';
import ProfileDataTab from './ProfileHub/ProfileDataTab';
import ExportManagementTab from './ProfileHub/ExportManagementTab';
import ProfileSidebar from './ProfileHub/ProfileSidebar';
import ProfileDiagnosticsModal from './ProfileHub/ProfileDiagnosticsModal';
import toast from 'react-hot-toast';
import './ProfileHub/ProfileHub.css';

const StudentProfileHub = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useFamilyContext();
  const profileDataTabRef = useRef(null);

  // Get active tab from URL param, default to 'core-data'
  const urlTab = searchParams.get('view');
  const validTabs = ['core-data', 'export-manage'];
  const initialTab = validTabs.includes(urlTab) ? urlTab : 'core-data';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [profileData, setProfileData] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [inconsistencies, setInconsistencies] = useState([]);
  const [impactForecasts, setImpactForecasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiConfidence, setAiConfidence] = useState(87); // Default confidence score
  const [isDiagnosticsOpen, setIsDiagnosticsOpen] = useState(false);
  const [diagnosticsInitialTab, setDiagnosticsInitialTab] = useState('missing');
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true
  });

  // Sync activeTab with URL parameter
  useEffect(() => {
    const urlTab = searchParams.get('view');
    if (urlTab && validTabs.includes(urlTab) && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [searchParams]);

  useEffect(() => {
    if (currentUser?.id) {
      loadProfileData();
    }
  }, [currentUser]);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      const data = await profileDataService.getCompleteProfile(currentUser.id);
      setProfileData(data);
      
      // Calculate completeness
      const completenessData = profileDataService.calculateCompleteness(data);
      setCompleteness(completenessData);
      
      // Check inconsistencies
      const inconsistencyData = profileDataService.checkConsistencies(data);
      setInconsistencies(inconsistencyData);
      
      // Generate impact forecasts
      const forecasts = profileDataService.generateImpactForecasts(data, completenessData.missingFields, completenessData);
      setImpactForecasts(forecasts);
      
      // Calculate AI confidence based on data completeness and quality
      calculateAIConfidence(data, completenessData);
      
      // Sync profile tasks with Epic Journey
      try {
        const result = await ProfileTaskGenerationService.syncProfileTasksWithEpic(
          supabase,
          currentUser.id
        );
        console.log('Profile tasks synced:', result);
        if (result.inserted > 0 || result.updated > 0) {
          toast.success(`Profile tasks updated: ${result.inserted} new, ${result.updated} updated`, {
            duration: 3000,
            position: 'bottom-right'
          });
        }
      } catch (error) {
        console.error('Error syncing profile tasks:', error);
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const calculateAIConfidence = (data, completenessData) => {
    // Section-weighted confidence calculation
    // Each section contributes based on its importance and completion percentage
    let confidence = 0;
    
    // Section weights (total = 100%)
    const sectionWeights = {
      assessment: 50,      // Binary - either have it or don't
      activities: 15,      // Critical for differentiation
      goals: 10,          // Shows direction and fit
      academicPerformance: 5,  // Important but standardized
      testScores: 5,      // Important but standardized
      coursework: 5,      // Important but standardized
      personalBackground: 5,   // Provides context
      basicInfo: 3,       // Necessary but basic
      schoolInfo: 2       // Necessary but basic
    };
    
    // Add assessment confidence (50% if completed)
    if (data.assessment?.responses) {
      confidence += sectionWeights.assessment;
    }
    
    // Add weighted confidence for each section based on completion
    if (completenessData?.sections) {
      Object.entries(completenessData.sections).forEach(([section, stats]) => {
        const weight = sectionWeights[section];
        if (weight && stats.percentage !== undefined) {
          // Add weighted percentage (e.g., 15% weight × 80% complete = 12%)
          confidence += (weight * stats.percentage / 100);
        }
      });
    }
    
    // Small penalty for data inconsistencies (max -5%)
    const errorCount = inconsistencies.filter(i => i.type === 'error').length;
    const warningCount = inconsistencies.filter(i => i.type === 'warning').length;
    confidence -= Math.min(5, errorCount * 2 + warningCount * 0.5);
    
    // Ensure confidence is between 0 and 100
    setAiConfidence(Math.max(0, Math.min(100, Math.round(confidence))));
  };

  const handleFieldUpdate = async (field, value, section) => {
    try {
      // Optimistically update local state first for smooth UX
      setProfileData(prevData => ({
        ...prevData,
        academic: {
          ...prevData.academic,
          [field]: value,
          updated_at: new Date().toISOString()
        }
      }));

      // Update in database in background
      await profileDataService.updateField(currentUser.id, field, value, section);
      
      // Recalculate completeness with updated data
      const updatedData = {
        ...profileData,
        academic: { ...profileData.academic, [field]: value }
      };
      const completenessData = profileDataService.calculateCompleteness(updatedData);
      setCompleteness(completenessData);
      
      toast.success('Field updated successfully');
    } catch (error) {
      console.error('Error updating field:', error);
      // Revert optimistic update on error by reloading data
      await loadProfileData();
      toast.error('Failed to update field');
    }
  };

  const handleOpenDiagnostics = (initialTab = 'missing') => {
    // Defensive validation to ensure initialTab is always a string
    const tabValue = typeof initialTab === 'string' ? initialTab : 'missing';
    
    // Debug logging to track what's being passed
    console.log(`handleOpenDiagnostics called with: ${initialTab} (${typeof initialTab})`);
    
    setDiagnosticsInitialTab(tabValue);
    setIsDiagnosticsOpen(true);
  };

  const handleNavigateToField = (field, sectionId) => {
    // Close the modal
    setIsDiagnosticsOpen(false);
    
    // Switch to the core data tab
    setActiveTab('core-data');
    
    // Expand the target section
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: true
    }));
    
    // Use the ref to trigger edit mode with focus after a brief delay for UI updates
    setTimeout(() => {
      if (profileDataTabRef.current && profileDataTabRef.current.startEditWithFocus) {
        // Call the imperative method to start editing and focus the field
        profileDataTabRef.current.startEditWithFocus(sectionId, field);
      } else {
        // Fallback to the old behavior if ref is not available
        const sectionElement = document.getElementById(sectionId);
        if (sectionElement) {
          sectionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          
          // Add highlight animation to the field
          const fieldElement = sectionElement.querySelector(`[data-field="${field}"]`);
          if (fieldElement) {
            fieldElement.classList.add('field-highlight');
            setTimeout(() => {
              fieldElement.classList.remove('field-highlight');
            }, 3000);
          }
        }
      }
    }, 100);
  };




  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading your complete profile...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-6 py-4 sm:py-6 lg:py-8">
        {/* Main Content Area */}
        <div>
          {activeTab === 'core-data' && (
            <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
              {/* ProfileDataTab - 3/5 width on desktop, full width on mobile/tablet */}
              <div className="w-full lg:w-3/5">
                <ProfileDataTab
                  ref={profileDataTabRef}
                  profileData={profileData}
                  completeness={completeness}
                  inconsistencies={inconsistencies}
                  onFieldUpdate={handleFieldUpdate}
                  onRefresh={loadProfileData}
                  expandedSections={expandedSections}
                  onSectionToggle={(sectionId, isExpanded) =>
                    setExpandedSections(prev => ({ ...prev, [sectionId]: isExpanded }))
                  }
                />
              </div>

              {/* ProfileSidebar - 2/5 width on desktop, full width on mobile/tablet */}
              <div className="w-full lg:w-2/5">
                <ProfileSidebar
                  completeness={completeness}
                  aiConfidence={aiConfidence}
                  inconsistencies={inconsistencies}
                  impactForecasts={impactForecasts}
                  profileData={profileData}
                  onFieldEdit={handleFieldUpdate}
                  onOpenDiagnostics={handleOpenDiagnostics}
                />
              </div>
            </div>
          )}

          {activeTab === 'export-manage' && (
            <ExportManagementTab
              profileData={profileData}
              completeness={completeness}
              inconsistencies={inconsistencies}
              onVerifyData={loadProfileData}
            />
          )}
          
        </div>
      </div>
      
      {/* Profile Diagnostics Modal */}
      <ProfileDiagnosticsModal
        isOpen={isDiagnosticsOpen}
        onClose={() => setIsDiagnosticsOpen(false)}
        completeness={completeness}
        inconsistencies={inconsistencies}
        aiConfidence={aiConfidence}
        onNavigateToField={handleNavigateToField}
        impactForecasts={impactForecasts}
        initialTab={diagnosticsInitialTab}
      />
    </div>
  );
};

export default StudentProfileHub;